/**
 *
 * ShelfOptionsSelector
 *
 */
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { RootState } from 'containers/Ribbon/CabinetRibbon/types';
import { SelectoOptionsArea } from 'containers/Ribbon/CabinetRibbon/CornerSelector';
import { makeSelectHistoryShelvesSelectedOption } from 'containers/Ribbon/ShelvesRibbon/ShelfCompositionSelector/selectors';
import SelectorRibbonOption from 'containers/Ribbon/CabinetRibbon/CornerSelector/CornerColor';
import emptyImg from 'assets/images/composition/enable/empty.svg';
import emptyImgDisabled from 'assets/images/composition/disabled/empty.svg';
import twoDoorsImg from 'assets/images/composition/enable/twoDoors.svg';
import fourDoorsImg from 'assets/images/composition/enable/fourDoors.svg';
import fourDoorsImgDisabled from 'assets/images/composition/disabled/fourDoors.svg';
import twoDoorsImgDisabled from 'assets/images/composition/disabled/twoDoors.svg';
import drawerImg from 'assets/images/composition/enable/drawers.svg';
import drawerImgDisabled from 'assets/images/composition/disabled/drawers.svg';
import {
  restoreCabinet,
  setComposition,
} from 'containers/Configurator/actions/furniture';
import {
  MINIMUM_LARGE_DOORS_HEIGHT,
  ShelfComposition,
} from 'lib/modelBuilder/constants';
import { Cabinet } from 'lib/modelBuilder/entities/Cabinet';
import {
  makeSelectLastHistoryCabinet,
  makeSelectLastHistoryFurniture,
} from 'containers/Configurator/selectors/history';
import { displayDrawersCompositionPanelAction } from 'containers/DrawersCompositionPanel/actions';
import { makeSelectCommonHeight } from 'containers/Ribbon/ShelvesRibbon/selectors';
import { useTranslation } from 'react-i18next';
import {
  findCombinations,
  getDrawersCount,
} from 'lib/modelBuilder/utils/combinationDrawers';
import { DRAWERS_COMPOSITION } from 'containers/DrawersCompositionPanel/constants';
import { makeSelectRules } from 'containers/Configurator/selectors/rules';
import { Rule } from 'lib/constraintsChecker/Rule';
import {
  FurnitureEditor,
  FurnitureEditorContext,
  FurnitureEditorStatus,
} from 'lib/constraintsChecker/FurnitureEditor';
import {
  makeSelectContext,
  makeSelectIsActiveCustomMode,
} from 'containers/Configurator/selectors/general';
import { Shelf } from 'lib/modelBuilder/entities/Shelf';
import { makeSelectShelvesLevelSelected } from 'containers/Configurator/selectors/selection';
import ReactTooltip from 'react-tooltip';
import { Furniture } from '../../../../lib/modelBuilder/entities/Furniture';
import { makeSelectSelectedIdCabinet } from '../../../Configurator/selectors/furniture';

interface StateProps {
  shelvesSelectedOption: ShelfComposition | undefined;
  cabinet: Cabinet;
  commomHeight: number | undefined;
  rules: Rule[] | null;
  context: FurnitureEditorContext;
  customMode: boolean;
  shelvesLevelSelected: number[];
  furniture: Furniture;
  selectedIdCabinet: string | null;
}

const stateSelector = createStructuredSelector<RootState, StateProps>({
  shelvesSelectedOption: makeSelectHistoryShelvesSelectedOption(),
  cabinet: makeSelectLastHistoryCabinet(),
  commomHeight: makeSelectCommonHeight(),
  rules: makeSelectRules(),
  context: makeSelectContext(),
  customMode: makeSelectIsActiveCustomMode(),
  shelvesLevelSelected: makeSelectShelvesLevelSelected(),
  furniture: makeSelectLastHistoryFurniture(),
  selectedIdCabinet: makeSelectSelectedIdCabinet(),
});

function ShelfCompositionSelector() {
  const dispatch = useDispatch();
  const {
    shelvesSelectedOption,
    cabinet,
    commomHeight,
    rules,
    context,
    customMode,
    shelvesLevelSelected,
    furniture,
    selectedIdCabinet,
  } = useSelector(stateSelector);
  const { t } = useTranslation();

  function checkComposition(
    composition: ShelfComposition,
  ): FurnitureEditor | null {
    if (rules === null) {
      return null;
    }
    const cabinetNext = cabinet.clone();
    let selectedShelves: Shelf[] = [];
    if (shelvesLevelSelected.length === 0)
      selectedShelves = cabinetNext.shelves;
    else
      selectedShelves = cabinetNext.shelves.filter(item =>
        shelvesLevelSelected.includes(item.level),
      );

    if (ShelfComposition.Drawer) {
      const filteredSelectedShelves = selectedShelves.filter(
        item => item.height < MINIMUM_LARGE_DOORS_HEIGHT,
      );
      if (filteredSelectedShelves.length > 0) {
        selectedShelves = filteredSelectedShelves;
      }
    }

    const cabinetEditor = new FurnitureEditor(
      furniture,
      selectedIdCabinet,
      rules,
      context,
      customMode,
    );

    selectedShelves.map(shelf => (shelf.composition = composition));

    cabinetEditor.checkRules(cabinetNext);

    if (cabinetEditor.status === FurnitureEditorStatus.Cancel)
      return cabinetEditor;
    return null;
  }

  const addDrawersCA = useMemo(
    () => checkComposition(ShelfComposition.Drawer),
    [cabinet, shelvesLevelSelected],
  );
  const addTwoDoorsCA = useMemo(
    () => checkComposition(ShelfComposition.TwoDoors),
    [cabinet, shelvesLevelSelected],
  );
  const addFourDoorsCA = useMemo(
    () => checkComposition(ShelfComposition.FourDoors),
    [cabinet, shelvesLevelSelected],
  );
  const emptyCA = useMemo(() => checkComposition(ShelfComposition.Open), [
    cabinet,
  ]);

  useEffect(() => ReactTooltip.rebuild(), [
    emptyCA,
    addTwoDoorsCA,
    addDrawersCA,
    addFourDoorsCA,
  ]);

  // const addDrawersCA = false;

  function onLeave() {
    // Restore the last history element
    dispatch(restoreCabinet(cabinet));
  }

  function onClickDrawer() {
    dispatch(setComposition(ShelfComposition.Drawer, false));

    if (commomHeight) {
      const combo = findCombinations(
        commomHeight,
        getDrawersCount(commomHeight, 4),
      );
      // Don't open the panel of combo if we have just one comob or
      // if we don't have combo for this height
      if (combo && combo.length > 1 && DRAWERS_COMPOSITION[commomHeight]) {
        dispatch(displayDrawersCompositionPanelAction(true));
      }
    }
  }

  return (
    <>
      <SelectoOptionsArea onMouseLeave={onLeave}>
        <SelectorRibbonOption
          imageSrc={!emptyCA ? emptyImg : emptyImgDisabled}
          title={t('Open')}
          selected={
            shelvesSelectedOption &&
            shelvesSelectedOption === ShelfComposition.Open
          }
          onClick={() => dispatch(setComposition(ShelfComposition.Open, false))}
          onHover={() => dispatch(setComposition(ShelfComposition.Open, true))}
          disabled={!!emptyCA}
          tooltip={emptyCA && emptyCA.message && emptyCA.message.content}
        />
        <SelectorRibbonOption
          imageSrc={!addTwoDoorsCA ? twoDoorsImg : twoDoorsImgDisabled}
          title={t('Two doors')}
          selected={
            shelvesSelectedOption &&
            shelvesSelectedOption === ShelfComposition.TwoDoors
          }
          onClick={() =>
            dispatch(setComposition(ShelfComposition.TwoDoors, false))
          }
          onHover={() =>
            dispatch(setComposition(ShelfComposition.TwoDoors, true))
          }
          disabled={!!addTwoDoorsCA}
          tooltip={
            addTwoDoorsCA &&
            addTwoDoorsCA.message &&
            addTwoDoorsCA.message.content
          }
        />
        <SelectorRibbonOption
          imageSrc={!addFourDoorsCA ? fourDoorsImg : fourDoorsImgDisabled}
          title={t('Four doors')}
          selected={
            shelvesSelectedOption &&
            shelvesSelectedOption === ShelfComposition.FourDoors
          }
          onClick={() =>
            dispatch(setComposition(ShelfComposition.FourDoors, false))
          }
          onHover={() =>
            dispatch(setComposition(ShelfComposition.FourDoors, true))
          }
          disabled={!!addFourDoorsCA}
          tooltip={
            addFourDoorsCA &&
            addFourDoorsCA.message &&
            addFourDoorsCA.message.content
          }
        />
        <SelectorRibbonOption
          imageSrc={addDrawersCA ? drawerImgDisabled : drawerImg}
          title={t('Drawers')}
          selected={
            shelvesSelectedOption &&
            shelvesSelectedOption === ShelfComposition.Drawer
          }
          onClick={onClickDrawer}
          onHover={() =>
            dispatch(setComposition(ShelfComposition.Drawer, true))
          }
          disabled={!!addDrawersCA}
          tooltip={
            addDrawersCA && addDrawersCA.message && addDrawersCA.message.content
          }
        />
      </SelectoOptionsArea>
      <ReactTooltip id="composition" place="bottom" type="warning" />
    </>
  );
}

export default ShelfCompositionSelector;
